import type { ControlledTagTerm, ItemDetail, ItemListItem } from '@/types';
import type { ItemFilterSearch } from '@/lib/validation';

const RELATED_TAG_GROUP_WEIGHTS: Record<string, number> = {
  role: 3,
  context: 2.5,
  interaction: 2.25,
  purpose: 2,
  tone: 1.25,
};

const RELATED_SEED_TAG_LIMIT = 3;

function getUniqueTagTerms(tags: ControlledTagTerm[]): ControlledTagTerm[] {
  const seen = new Set<string>();

  return tags.filter((tag) => {
    if (seen.has(tag.slug)) {
      return false;
    }

    seen.add(tag.slug);
    return true;
  });
}

function serializeTagSearch(tags: string[]): string | undefined {
  const normalized = Array.from(new Set(tags.map((tag) => tag.trim()).filter(Boolean))).sort();
  return normalized.length > 0 ? normalized.join(',') : undefined;
}

function collectRelatedSeedTags(item: ItemDetail): string[] {
  const seeds: string[] = [];
  const usedGroups = new Set<string>();

  if (item.primaryRole?.slug) {
    seeds.push(item.primaryRole.slug);
    usedGroups.add(item.primaryRole.group);
  }

  for (const tag of getUniqueTagTerms(item.tags)) {
    if (seeds.length >= RELATED_SEED_TAG_LIMIT) {
      break;
    }

    if (seeds.includes(tag.slug)) {
      continue;
    }

    if (usedGroups.has(tag.group) && tag.group !== 'role') {
      continue;
    }

    seeds.push(tag.slug);
    usedGroups.add(tag.group);
  }

  return seeds;
}

export function buildRelatedItemsSearch(item: ItemDetail | null | undefined): ItemFilterSearch {
  if (!item) {
    return {
      type: undefined,
      ui: undefined,
      tags: undefined,
    };
  }

  return {
    type: item.type,
    ui: undefined,
    tags: serializeTagSearch(collectRelatedSeedTags(item)),
  };
}

export function getRelatedItemScore(currentItem: ItemDetail, candidate: ItemListItem): number {
  if (candidate.id === currentItem.id) {
    return 0;
  }

  let score = 0;

  if (candidate.type === currentItem.type) {
    score += 2;
  }

  if (
    candidate.primaryRole?.slug
    && currentItem.primaryRole?.slug
    && candidate.primaryRole.slug === currentItem.primaryRole.slug
  ) {
    score += 6;
  }

  const currentTagMap = new Map(currentItem.tags.map((tag) => [tag.slug, tag]));

  for (const candidateTag of candidate.tags) {
    const matchedCurrentTag = currentTagMap.get(candidateTag.slug);
    if (!matchedCurrentTag) {
      continue;
    }

    score += RELATED_TAG_GROUP_WEIGHTS[matchedCurrentTag.group] ?? 1;
  }

  if (candidate.isFeatured) {
    score += 0.5;
  }

  return score;
}
